import { useEffect } from 'react';
import { useAolStore } from '../../context/AolContext';
import { useEventStore } from '../../context/EventContext';
import useApiEndpoints from '../../context/useApiEndpoints';
import { useVesselStore } from '../../context/VesselContext';
import { makeAolDataItemList, makeAolGroupList } from '../../factory/Aol/AolDataItemFactory';
import { AolItemFactory } from '../../factory/AolItemFactory';
import { ValidatedNoonReportFactory } from '../../factory/ValidatedNoonReportFactory';
import { VoyageFuelPriceFactory } from '../../factory/VoyageFuelPriceFactory';
import { AolItemBM } from '../../types/BusinessModel/AolItemBM';
import { ValidatedNoonReportBM } from '../../types/BusinessModel/ValidatedNoonReportBM';
import { VoyageFuelPriceBM } from '../../types/BusinessModel/VoyageFuelPriceBM';
import { TankersEvent } from '../../types/ViewModel/TankersEvent';
import { buildQueryParams } from '../endpoints/queryParamsHelper';
import useApi from '../useApi';
import { NoonReportSectionEnum } from '../../types/BusinessModel/AolEnums';
import { addDateDays, getUtcTimeNow, isDateAfter, isDateSameOrBefore } from 'odfjell-date-time-utils/dateTimeUtils';
import apiEndpointUtils from 'src/utils/apiEndpointUtils';
import useAppFeature from 'src/app/useAppFeature';
import { formatAolUrlDate, getAolDateFromUrlFormat } from 'src/utils/aol/aolDateUtils';
import { useStowagePlanStore } from 'src/context/StowagePlanContext';
import { requestThrottleManager } from 'src/utils/requestThrottleManager';
import { generateLocalAolItems } from 'src/utils/aol/localAolGenerator';
import { AppFeatureEnum } from 'src/enums/appFeatureEnum';
import { useRotationStore } from 'src/context/RotationContext';
import { deriveLocalPortCalls } from 'src/utils/aol/deriveLocalPortCalls';
import TankersEventFactory from '../../factory/TankersEventFactory';
import { getAllReportEventsItems } from '../report-events/offlineReportEventsApi';
import { TimeZoneDataBM } from 'src/types/BusinessModel/TimeZoneDataBM';
import { TimeZoneFactory } from 'src/factory/TimeZoneFactory';
import { TimeZoneData } from 'src/types/ViewModel/TimeZoneData';
import { RotationPortCallBM } from 'src/types/BusinessModel/Stowage/Rotation/RotationPortCallBM';
import { RotationPortCall } from 'src/types/ViewModel/Stowage/Rotation/RotationPortCall';
import { LocalPortCall } from 'src/utils/aol/localAolGenerator';
import { PortNameBM } from 'src/types/BusinessModel/Stowage/Port/PortNameBM';
import { createPortNameList } from 'src/factory/Stowage/Port/PortNameFactory';

export default () => {
    const { apiEndpoints } = useApiEndpoints();
    const { processApiEndpointError } = apiEndpointUtils();
    const aolItemFactory = new AolItemFactory();
    const validatedNoonReportFactory = new ValidatedNoonReportFactory();
    const voyageFuelPriceFactory = new VoyageFuelPriceFactory();
    const { state, getApiData } = useApi();
    const {
        setLastAolFetchTime,
        lastAolFetchTime,
        setAolItems,
        setNoonReports,
        setAolDataItems,
        setAolGroups,
        setVoyageFuelPrices,
        aolEventsArray,
        setAolEventsArray,
        aolEventsVesselImo,
        setAolEventsVesselImo,
        aolEventsLoadedRange,
        setAolEventsLoadedRange,
        aolGroups,
        aolDataItems,
    } = useAolStore();
    const { lastDeletEventDate, lastEventChangeDate } = useEventStore();
    const { selectedVessel } = useVesselStore();
    const { loadedMasterStowagePlan } = useStowagePlanStore();
    const { isFeatureEnabled } = useAppFeature();
    const { imosRotationPortCallList, timeZoneData, setTimeZoneData, setImosRotationPortCallList, portNameList, setPortNameList } =
        useRotationStore();
    const { newEventList } = TankersEventFactory();

    const ensureAolEventsArrayLoaded = async (
        imoNumber: number,
        fromDate: string,
        toDate: string,
        forceRefresh?: boolean
    ): Promise<TankersEvent[] | null> => {
        const isStowageIntegrationActive = isFeatureEnabled({ featureEnum: AppFeatureEnum.StowageIntegration, imoNumber });

        if (!isStowageIntegrationActive) return null;

        const hasAolEventsArray = aolEventsArray && aolEventsArray.length > 0;
        const isVesselMismatch = aolEventsVesselImo !== null && aolEventsVesselImo !== imoNumber;
        const isDateRangeMismatch = aolEventsLoadedRange !== null &&
            (aolEventsLoadedRange.from !== fromDate || aolEventsLoadedRange.to !== toDate);

        if (!hasAolEventsArray || forceRefresh || isVesselMismatch || isDateRangeMismatch || (!lastAolFetchTime && hasAolEventsArray)) {
            return await loadAolEventsArray(imoNumber, fromDate, toDate, forceRefresh || isVesselMismatch);
        }

        return aolEventsArray;
    };

    const loadAolEventsArray = async (
        imoNumber: number,
        fromDate: string,
        toDate: string,
        forceRefreshCache = false
    ): Promise<TankersEvent[] | null> => {
        const fromDateObj = getAolDateFromUrlFormat(fromDate);
        const toDateObj = getAolDateFromUrlFormat(toDate);

        const fromOffsetStr = Math.floor(addDateDays(fromDateObj, -7).getTime() / 1000).toString();
        const toOffsetStr = Math.floor(addDateDays(toDateObj, 2).getTime() / 1000).toString();

        const requestKey = `loadAolEventsArray:${imoNumber}:${fromOffsetStr}:${toOffsetStr}`;

        return requestThrottleManager.execute(requestKey, async () => {
            try {
                const result = await getApiData({
                    endpoint: apiEndpoints['getTankersEventDataForAols'].url,
                    queryParam: buildQueryParams({
                        imoNumber,
                        fromOffsetStr,
                        toOffsetStr,
                    }),
                    forceRefreshCache,
                });

                if (result && result.events) {
                    const offlineReportEvents = await getAllReportEventsItems();
                    const evList = newEventList(result, offlineReportEvents, false);

                    if (evList) {
                        const eventArray = [...evList];
                        setAolEventsArray(eventArray);
                        setAolEventsVesselImo(imoNumber);
                        setAolEventsLoadedRange({ from: fromDate, to: toDate });
                        return eventArray;
                    }
                }
                return null;
            } catch (error) {
                console.error('Failed to load AOL events array:', error);
                return null;
            }
        });
    };

    const fetchTimeZoneData = async (portIds: number[]): Promise<TimeZoneData | null> => {
        try {
            const result = await getApiData({
                endpoint: apiEndpoints['getTimeZoneData'].url,
                queryParam: buildQueryParams({ portIds }),
            });

            if (result) {
                const timeZoneDataBM = result as TimeZoneDataBM;
                const timeZoneFactory = new TimeZoneFactory();
                const tzData = timeZoneFactory.newTimeZoneData(timeZoneDataBM);
                setTimeZoneData(tzData);
                return tzData;
            }
            return null;
        } catch (error) {
            console.error('Failed to fetch timezone data:', error);
            return null;
        }
    };

    /**
     * Fetches IMOS rotation data and converts it to RotationPortCall[] for use in AOL generation.
     * Only fetches voyages up to lastVoyageNumber (the firstVoyageSync cutoff) so we don't
     * load data already covered by the stowage plan rotation.
     * Results are stored in RotationContext for subsequent calls.
     */
    const fetchImosRotationForAol = async (imoNumber: number, lastVoyageNumber?: number): Promise<RotationPortCall[] | null> => {
        try {
            const result = await getApiData({
                endpoint: apiEndpoints['getRotation'].url,
                queryParam: buildQueryParams({ imoNumber, ...(lastVoyageNumber ? { lastVoyageNumber } : {}) }),
                additionalInfo: { hideLoader: true },
            });

            if (!result) return null;

            const bms = result as RotationPortCallBM[];

            // Ensure port names are loaded for display name resolution
            let _portNameList = portNameList;
            if (!_portNameList) {
                const portNamesResult = await getApiData({
                    endpoint: apiEndpoints['getPortNames'].url,
                    additionalInfo: { hideLoader: true },
                });
                if (portNamesResult) {
                    _portNameList = createPortNameList(portNamesResult as PortNameBM[]);
                    setPortNameList([..._portNameList]);
                }
            }

            // Convert BMs to lightweight RotationPortCall objects with only the fields
            // needed by deriveLocalPortCalls: imosPortCallId, portDisplayName, voyageNumber,
            // imosPortFunction, portId, berthCalls (imosBerthCallId, berthId)
            const rotationPortCalls: RotationPortCall[] = bms.map(
                (bm) =>
                    ({
                        imosPortCallId: bm.portCallId,
                        portDisplayName: _portNameList?.find((p) => p.portId === bm.portId)?.name ?? bm.portDisplayName ?? '',
                        voyageNumber: bm.voyageNumber,
                        imosPortFunction: bm.imosPortFunction,
                        portId: bm.portId,
                        berthCalls:
                            bm.berthCalls?.map((bc) => ({
                                imosBerthCallId: bc.berthCallId,
                                berthId: bc.berthId,
                            })) || [],
                    } as unknown as RotationPortCall)
            );

            setImosRotationPortCallList([...rotationPortCalls]);
            return rotationPortCalls;
        } catch (error) {
            console.error('Failed to fetch IMOS rotation for AOL:', error);
            return null;
        }
    };

    const getAolDataSinceLastUpdate = async (imoNumber: number, fromDate: string, toDate: string, forceRefreshCache?: boolean | null) => {
        if (!lastAolFetchTime) {
            await ensureAolEventsArrayLoaded(imoNumber, fromDate, toDate, true);
            getAolData(imoNumber, fromDate, toDate, forceRefreshCache);
            return;
        }

        const hasAolDataLoaded = aolGroups && aolGroups.length > 0 && aolDataItems && aolDataItems.length > 0;

        const hasAolEventsArray = aolEventsArray && aolEventsArray.length > 0;
        let lastEvent: TankersEvent | null = null;
        let hasNewData = false;

        if (hasAolEventsArray) {
            lastEvent = aolEventsArray.sort((a, b) => (isDateSameOrBefore(a.lastUpdated, b.lastUpdated, 'second') ? -1 : 1))[
                aolEventsArray.length - 1
            ];
            hasNewData = lastEvent ? isDateAfter(lastEvent.lastUpdated, lastAolFetchTime, 'second') : false;
        }

        if (!hasNewData && lastEventChangeDate) {
            hasNewData = isDateAfter(lastEventChangeDate, lastAolFetchTime, 'second');
        }

        if (!hasNewData && lastDeletEventDate) {
            hasNewData = isDateAfter(lastDeletEventDate, lastAolFetchTime, 'second');
        }

        if (!hasNewData && hasAolDataLoaded) {
            await ensureAolEventsArrayLoaded(imoNumber, fromDate, toDate, forceRefreshCache);
            return;
        }

        await ensureAolEventsArrayLoaded(imoNumber, fromDate, toDate, true);
        getAolData(imoNumber, fromDate, toDate, true);
    };

    const getAolData = async (imoNumber: number, fromDate: string, toDate: string, forceRefreshCache?: boolean | null) => {
        setAolGroups([]);
        const _fromDate = formatAolUrlDate(addDateDays(getAolDateFromUrlFormat(fromDate), -1));
        const aolItemsPromise = getAolItems(imoNumber, _fromDate, toDate, forceRefreshCache);
        const noonReportsPromise = getNoonReports(imoNumber, _fromDate, toDate, forceRefreshCache);

        const values = await Promise.all([aolItemsPromise, noonReportsPromise]);
        if (!values[0] || !values[1]) return;
        const _aolDataItems = await makeAolDataItemList(values[0], values[1], isFeatureEnabled, selectedVessel, loadedMasterStowagePlan);
        const _aolGroups = makeAolGroupList(_aolDataItems, selectedVessel);
        setAolItems([...values[0]]);
        setNoonReports([...values[1]]);
        setAolGroups([..._aolGroups]);
        setAolDataItems([..._aolDataItems]);
    };

    const getAolItems = async (imoNumber: number, fromDate: string, toDate: string, forceRefreshCache?: boolean | null) => {
        const _fromDate = getAolDateFromUrlFormat(fromDate);
        const _toDate = getAolDateFromUrlFormat(toDate);

        // Extend event fetch range to cover the most recent port call before the display range.
        // Without this, the EOSP event that starts the timeline may fall outside the default
        // fetch buffer, causing a truncated/invalid timeline (missing dates, broken AOL items).
        let eventFetchFromDate = fromDate;
        const rotationForEventRange = loadedMasterStowagePlan?.rotation;
        if (rotationForEventRange && rotationForEventRange.length > 0) {
            let mostRecentArrivalBeforeRange: Date | null = null;
            for (const pc of rotationForEventRange) {
                if (!pc.arrivalDateTimeUtc) continue;
                const arrival = new Date(pc.arrivalDateTimeUtc);
                if (arrival <= _fromDate) {
                    if (!mostRecentArrivalBeforeRange || arrival > mostRecentArrivalBeforeRange) {
                        mostRecentArrivalBeforeRange = arrival;
                    }
                }
            }
            if (mostRecentArrivalBeforeRange) {
                const defaultFetchStart = addDateDays(getAolDateFromUrlFormat(fromDate), -7);
                if (mostRecentArrivalBeforeRange < defaultFetchStart) {
                    eventFetchFromDate = formatAolUrlDate(addDateDays(mostRecentArrivalBeforeRange, -2));
                }
            }
        }

        const currentEventArray = await ensureAolEventsArrayLoaded(imoNumber, eventFetchFromDate, toDate, forceRefreshCache ?? false);

        if (!currentEventArray || !currentEventArray.length) {
            return [];
        }

        const stowagePlanPortCalls = loadedMasterStowagePlan?.rotation || null;

        // Ensure timezone data is loaded for DST calculations
        // Ignore stale closure values when force-refreshing or when cached data belongs to a different vessel
        const isVesselMismatch = aolEventsVesselImo !== null && aolEventsVesselImo !== imoNumber;
        let effectiveTimeZoneData = (forceRefreshCache || isVesselMismatch) ? null : timeZoneData;
        if (!effectiveTimeZoneData && stowagePlanPortCalls && stowagePlanPortCalls.length > 0) {
            const portIds = Array.from(new Set(stowagePlanPortCalls.map((pc) => pc.portId)));
            effectiveTimeZoneData = await fetchTimeZoneData(portIds);
        }

        // Ensure IMOS rotation is loaded.
        // For standard vessels: the stowage plan rotation covers voyages from firstVoyageOrcaWebStowageImosSync onwards,
        // so IMOS data is only needed for older "legacy" voyages.
        // For simple mode vessels: the stowage plan may not be loaded at all (no rotation data),
        // so IMOS rotation is the only source of port call data and must always be fetched.
        // Ignore stale IMOS rotation when force-refreshing or vessel mismatch
        let effectiveImosRotation = (forceRefreshCache || isVesselMismatch) ? null : imosRotationPortCallList;
        if (!effectiveImosRotation || effectiveImosRotation.length === 0) {
            const isSimpleMode = selectedVessel?.hasOrcaWebSimpleMode ?? false;

            if (isSimpleMode && (!stowagePlanPortCalls || stowagePlanPortCalls.length === 0)) {
                // Simple mode vessels without stowage plan rotation need IMOS rotation for all voyages
                effectiveImosRotation = await fetchImosRotationForAol(imoNumber);
            } else {
                const firstVoyageSync = selectedVessel?.firstVoyageOrcaWebStowageImosSync ?? 0;
                const hasLegacyEvents =
                    firstVoyageSync > 0 && currentEventArray.some((e) => e.voyageNumber != null && +e.voyageNumber < firstVoyageSync);
                if (hasLegacyEvents) {
                    effectiveImosRotation = await fetchImosRotationForAol(imoNumber, firstVoyageSync);
                }
            }
        }

        // Extend timezone data to cover IMOS rotation ports (may include ports not in stowage plan)
        if (effectiveImosRotation && effectiveImosRotation.length > 0) {
            const imosPortIds = Array.from(new Set(effectiveImosRotation.map((pc) => pc.portId)));
            const stowagePortIds = stowagePlanPortCalls ? stowagePlanPortCalls.map((pc) => pc.portId) : [];
            const missingPortIds = imosPortIds.filter((id) => !stowagePortIds.includes(id));
            if (missingPortIds.length > 0 && effectiveTimeZoneData) {
                const missingIds = missingPortIds.filter((id) => !effectiveTimeZoneData![id]);
                if (missingIds.length > 0) {
                    const allPortIds = Array.from(new Set(stowagePortIds.concat(imosPortIds)));
                    effectiveTimeZoneData = await fetchTimeZoneData(allPortIds);
                }
            } else if (!effectiveTimeZoneData) {
                const allPortIds = Array.from(new Set(stowagePortIds.concat(imosPortIds)));
                if (allPortIds.length > 0) {
                    effectiveTimeZoneData = await fetchTimeZoneData(allPortIds);
                }
            }
        }

        const portCalls = deriveLocalPortCalls(effectiveImosRotation, stowagePlanPortCalls, effectiveTimeZoneData);

        // Generate local AOLs for all voyages
        const localBms = generateLocalAolItems({
            events: currentEventArray,
            portCalls,
            vessel: selectedVessel,
            fromDate: _fromDate,
            toDate: _toDate,
        });

        const filteredLocalBms = localBms.filter((bm: AolItemBM) => {
            const voyageNum = bm.voyageNumber ? parseInt(bm.voyageNumber, 10) : null;
            return voyageNum !== null;
        });

        const localItems = aolItemFactory.createList(filteredLocalBms);

        localItems.sort((a, b) => {
            const voyageA = parseInt(a.voyageNumber, 10) || 0;
            const voyageB = parseInt(b.voyageNumber, 10) || 0;
            if (voyageA !== voyageB) return voyageA - voyageB;

            const dateA = new Date(a.portLogAolItem?.startTimeLocalTime || 0).getTime();
            const dateB = new Date(b.portLogAolItem?.startTimeLocalTime || 0).getTime();
            return dateA - dateB;
        });

        setLastAolFetchTime(getUtcTimeNow());
        return localItems;
    };

    const getNoonReports = async (imoNumber: number, fromDate: string, toDate: string, forceRefreshCache?: boolean | null) => {
        const requestKey = `getNoonReportsForShip:${imoNumber}:${fromDate}:${toDate}`;

        return requestThrottleManager.execute(requestKey, async () => {
            const result = await getApiData({
                endpoint: apiEndpoints['getNoonReportsForShip'].url,
                queryParam: buildQueryParams({ imoNumber, fromDate, toDate }),
                forceRefreshCache,
            });

            if (result) {
                const mapped = validatedNoonReportFactory.createList(result);
                return mapped;
            }
            return null;
        });
    };

    const getNoonReportSection = async (imoNumber: number, noonReportId: number, section: NoonReportSectionEnum, forceRefreshCache?: boolean, noonReportGuid?: string) => {
        const result = await getApiData({
            endpoint: apiEndpoints['getNoonReportSection'].url,
            queryParam: buildQueryParams({ imoNumber, noonReportId, section, noonReportGuid }),
            forceRefreshCache,
        });

        if (result) return result as ValidatedNoonReportBM;
    };

    const getVoyageFuelPrices = (imoNumber: number) =>
        getApiData({
            endpoint: apiEndpoints['getVoyageFuelPrices'].url,
            queryParam: buildQueryParams({ imoNumber }),
        });

    const getDistanceBetween = async (imoNumber: number, fromDate: string, toDate: string) => {
        const result = await getApiData({
            endpoint: apiEndpoints['getDistanceBetween'].url,
            queryParam: buildQueryParams({ imoNumber, from: fromDate, to: toDate }),
            additionalInfo: { hideLoader: true },
            callInBackground: true,
        });

        if (result) {
            const distanceBetween = result as number;
            return distanceBetween;
        }
    };

    useEffect(() => {
        if (state.status === 'success') {
            if (state.endpoint === apiEndpoints['getNoonReportsForShip'].url) {
                const bms = state.result as ValidatedNoonReportBM[];
                const noonReports = validatedNoonReportFactory.createList(bms);
                setNoonReports([...noonReports]);
            }

            if (state.endpoint === apiEndpoints['getVoyageFuelPrices'].url) {
                const bms = state.result as VoyageFuelPriceBM[];
                const voyageFuelPrices = voyageFuelPriceFactory.createList(bms);
                setVoyageFuelPrices([...voyageFuelPrices]);
            }
        } else {
            processApiEndpointError(state);
        }
    }, [state]);

    return {
        state,
        getAolItems,
        getNoonReports,
        getNoonReportSection,
        getAolDataSinceLastUpdate,
        getAolData,
        getVoyageFuelPrices,
        getDistanceBetween,
        ensureAolEventsArrayLoaded,
    };
};
